import type { LDClient } from 'launchdarkly-react-client-sdk';
import type { User } from '@auth0/auth0-react';

import { notifyLaunchDarklyIdentified } from '@/state/launch-darkly.state';
import { ATLAS_USER_IDENTIFIER_CLAIM } from '@/utils/auth-claims';

import { readBranchCookie } from './branch-cookie';
import { LDMultiContext, LDScopeData } from './launch-darkly-types';

/**
 * Type guard to check if a context is a multi-context
 */
function isMultiContext(context: any): context is LDMultiContext {
  return context && context.kind === 'multi';
}

/**
 * User-context attributes describing this browser's Labs prototype assignment.
 * Shared by every LaunchDarkly context builder so opt-outs stay visible and segmentable.
 * Flag rules should not key off these: the SPA, not LaunchDarkly, enforces opt-out.
 */
export function prototypeUserAttributes(): { gaiaPrototypeOptOut: boolean } {
  return { gaiaPrototypeOptOut: readBranchCookie()?.status === 'labsOptOut' };
}

/**
 * Checks if the current LaunchDarkly context is anonymous
 */
export function isAnonymousContext(ldClient: LDClient | undefined): boolean {
  if (!ldClient) return true;

  try {
    const context = ldClient.getContext();
    if (!context) return true;

    // Check if it's a multi-context with user
    if (isMultiContext(context) && context.user) {
      return context.user.anonymous === true;
    }

    // For single user context, check if it's anonymous
    if (context && typeof context === 'object' && 'anonymous' in context) {
      return (context as any).anonymous === true;
    }

    return true;
  } catch (error) {
    console.error('[LaunchDarkly] Error checking context:', error);
    return true;
  }
}

/** Resolved inputs for the Gaia LaunchDarkly multi-context (hook + route guard). */
export type LDContextInputs = {
  email?: string;
  /** Atlas user identifier claim — required to include the user kind. */
  userId?: string;
  name?: string;
  organizationId?: string;
  facilityId?: string;
  /** Facility URL slug — LD agent rules target slugs, not agent UUIDs. */
  facilityUrlName?: string;
};

/**
 * Builds the LaunchDarkly multi-context Gaia sends today.
 * User kind is included only when `userId` (atlas claim) is present — same gate as the hook.
 */
export function buildLDMultiContext(inputs: LDContextInputs): LDMultiContext | null {
  const context: LDMultiContext = { kind: 'multi' };
  let hasContext = false;

  if (inputs.userId) {
    context.user = {
      key: inputs.email ?? inputs.userId,
      email: inputs.email,
      userId: inputs.userId,
      name: inputs.name,
      ...prototypeUserAttributes(),
    };
    hasContext = true;
  }

  if (inputs.organizationId) {
    context.organization = { key: inputs.organizationId };
    hasContext = true;
  }

  if (inputs.facilityId) {
    context.facility = { key: inputs.facilityId };
    hasContext = true;
  }

  // `agent.key` must be the facility URL slug, NOT the agent UUID.
  if (inputs.facilityUrlName) {
    context.agent = { key: inputs.facilityUrlName };
    hasContext = true;
  }

  return hasContext ? context : null;
}

/**
 * Builds a LaunchDarkly context from auth user and facility data.
 * Uses the atlas user identifier claim for `user.userId` (same as useLaunchDarklyContext).
 * `facilityData.facilityUrlName` is the facility URL slug (LD agent key), not the agent UUID.
 * @returns A LaunchDarkly multi-context object or null if nothing can be set
 */
export function buildLDContextFromAuth(
  user: User | undefined,
  facilityData: LDScopeData
): LDMultiContext | null {
  if (!user) return null;

  const atlasUserId = user[ATLAS_USER_IDENTIFIER_CLAIM] as string | undefined;
  // Same gate as useLaunchDarklyContext — never identify org/facility alone.
  if (!atlasUserId) return null;

  return buildLDMultiContext({
    email: user.email,
    userId: atlasUserId,
    name: user.name || user.email,
    organizationId: facilityData.organizationId,
    facilityId: facilityData.facilityId,
    facilityUrlName: facilityData.facilityUrlName,
  });
}

/**
 * Identifies user in LaunchDarkly with timeout
 * @param ldClient - The LaunchDarkly client instance
 * @param context - The context object to identify
 * @param timeoutMs - Timeout in milliseconds (default: 3000)
 * @returns Promise that resolves to true if identification succeeded, false otherwise
 */
export async function identifyWithTimeout(
  ldClient: LDClient,
  context: any,
  timeoutMs: number = 3000
): Promise<boolean> {
  try {
    const identifyPromise = ldClient.identify(context);
    // Notify on the real completion, even if it lands after the timeout below.
    identifyPromise.then(notifyLaunchDarklyIdentified, () => {});
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Identification timeout')), timeoutMs)
    );

    await Promise.race([identifyPromise, timeoutPromise]);
    return true;
  } catch (error) {
    console.error('[LaunchDarkly] Identification failed or timed out:', error);
    return false;
  }
}
